<template>
  <div class="medsync-opportunities-page">
    <card title="MedSync Opportunities">
      <template #header-actions>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadOpportunities"
          :disabled="loading"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </b-button>
      </template>

      <!-- Filters Section -->
      <b-row class="mb-3">
        <b-col md="3">
          <label for="timeframe-filter">Timeframe</label>
          <b-form-select
            id="timeframe-filter"
            v-model="filters.daysAhead"
            :options="timeframeOptions"
            @change="handleFilterChange"
          ></b-form-select>
        </b-col>

        <b-col md="3">
          <label for="med-count-filter"># of Medications</label>
          <b-form-select
            id="med-count-filter"
            v-model="filters.medicationRange"
            :options="medicationCountOptions"
            @change="handleFilterChange"
          ></b-form-select>
        </b-col>

        <b-col md="3">
          <label for="date-span-filter">Date Range Span</label>
          <b-form-select
            id="date-span-filter"
            v-model="filters.dateSpanRange"
            :options="dateSpanOptions"
            @change="handleDateSpanChange"
          ></b-form-select>
        </b-col>

        <b-col md="3">
          <label for="search-filter">Search Patient</label>
          <b-form-input
            id="search-filter"
            v-model="searchTerm"
            placeholder="Name or ID..."
            @input="handleSearchChange"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Data Table -->
        <b-table
                :items="opportunities"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="handleSortChange"
                striped
                show-empty
                :empty-html="emptyHtml"
                :busy="loading"
        >
          <template #table-busy>
              <div v-if="loading" class="text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-2">Loading opportunities...</p>
              </div>
          </template>
        <!-- Patient Name with Link -->
        <template #cell(patientName)="data">
          <a
            :href="`/processing/patient-dashboard/${data.item.patientId}`"
            target="_blank"
            class="font-weight-bold"
          >
            {{ data.item.patientName }}
          </a>
        </template>

        <!-- Next Fill Date -->
        <template #cell(nextFillDate)="data">
          {{ formatDate(data.item.nextFillDate) }}
        </template>

        <!-- Potential Anchor Date -->
        <template #cell(potentialAnchorDate)="data">
          {{ formatDate(data.item.potentialAnchorDate) }}
        </template>

        <!-- Date Range Span with Badge -->
        <template #cell(dateRangeSpan)="data">
          <b-badge :variant="getDateSpanVariant(data.item.dateRangeSpan)">
            {{ data.item.dateRangeSpan }} days
          </b-badge>
        </template>

        <!-- Last Offered Date -->
        <template #cell(lastOfferedDate)="data">
          <span v-if="data.item.lastOfferedDate">
            {{ formatDate(data.item.lastOfferedDate) }}
          </span>
          <span v-else class="text-muted">Never</span>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <div class="actions-grid">
            <b-button
              variant="success"
              class="action-btn"
              @click="markContacted(data.item)"
              title="Mark Contacted"
            >
              <i class="fa fa-phone action-icon"></i>
              <span class="action-label">Contacted</span>
            </b-button>
            <b-button
              variant="primary"
              class="action-btn"
              @click="enrollPatient(data.item)"
              title="Enroll in MedSync"
            >
              <i class="fa fa-calendar-check-o action-icon"></i>
              <span class="action-label">Enroll</span>
            </b-button>
            <b-button
              variant="danger"
              class="action-btn"
              @click="optOutPatient(data.item)"
              title="Opt Out of MedSync"
            >
              <i class="fa fa-ban action-icon"></i>
              <span class="action-label">Opt Out</span>
            </b-button>
          </div>
        </template>
      </b-table>
        <BottomPagerBar v-model="currentPage"
                :per-page="perPage"
                :total-rows="totalRows"
                :filtered-rows="searchTerm ? totalRows : undefined"
                :loading="loading"
                show-total
                show-page-size-selector
                item-name="opportunity"
                @change-page-size="handlePageSizeChange"
                @input="handlePageChange"
        />
    </card>

    <!-- Mark Contacted Modal -->
    <b-modal
      v-model="showContactModal"
      title="Mark Patient Contacted"
      @ok="saveContact"
      @hidden="resetContactModal"
    >
      <p>
        Recording that you offered MedSync enrollment to
        <strong>{{ selectedOpportunity?.patientName }}</strong>.
      </p>
      <b-form-group label="Notes (optional)">
        <b-form-textarea
          v-model="contactNotes"
          rows="3"
          placeholder="Add any notes about the contact attempt..."
        ></b-form-textarea>
      </b-form-group>
    </b-modal>

    <!-- MedSync Enrollment Modal -->
    <b-modal
      v-model="showEnrollmentModal"
      id="medsync-enrollment-modal"
      size="xl"
      title="MedSync Enrollment"
      hide-footer
      @hidden="closeEnrollmentModal"
    >
      <med-sync-enrollment-modal
        v-if="showEnrollmentModal && enrollmentPatient"
        :patient="enrollmentPatient"
        @completed="handleEnrollmentSuccess"
      />
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { debounce } from 'throttle-debounce';
import Card from '@/components/Cards/Card.vue';
import MedSyncEnrollmentModal from '@/components/Patient/MedSyncEnrollmentModal.vue';
import { Patient } from '@/models/Patient';
import moment from 'moment';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import { MedSyncOpportunity } from "@/models/MedSync";

interface MedicationRange {
  min: number;
  max: number | null;
}

interface Filters {
  daysAhead: number;
  medicationRange: MedicationRange | null;
  dateSpanRange: string;
  minDateSpan: number | null;
  maxDateSpan: number | null;
}

@Component({
  components: {
      BottomPagerBar,
    Card,
    MedSyncEnrollmentModal
  }
})
export default class MedSyncOpportunitiesPage extends Vue {
  opportunities: MedSyncOpportunity[] = [];
  totalRows = 0;
  loading = false;
  searchTerm = '';
  sortBy = 'nextFillDate';
  sortDesc = false;
  currentPage = 1;
  perPage = 50;

  filters: Filters = {
    daysAhead: 14,
    medicationRange: null,
    dateSpanRange: 'all',
    minDateSpan: null,
    maxDateSpan: null
  };

  // Contact Modal
  showContactModal = false;
  selectedOpportunity: MedSyncOpportunity | null = null;
  contactNotes = '';

  // Enrollment Modal
  showEnrollmentModal = false;
  enrollmentPatient: Patient | null = null;

  fields = [
    { key: 'patientName', label: 'Patient Name', sortable: true, sortKey: 'PatientName' },
    { key: 'eligibleMedCount', label: '# of Eligible Meds', sortable: true, sortKey: 'EligibleMedCount' },
    { key: 'nextFillDate', label: 'Next Fill Date', sortable: true, sortKey: 'NextFillDate' },
    { key: 'potentialAnchorDate', label: 'Potential Anchor Date', sortable: true, sortKey: 'PotentialAnchorDate' },
    { key: 'dateRangeSpan', label: 'Date Range Span', sortable: true, sortKey: 'DateRangeSpan' },
    { key: 'lastOfferedDate', label: 'Last Offered', sortable: true, sortKey: 'LastOfferedDate' },
    { key: 'actions', label: '', sortable: false }
  ];

  timeframeOptions = [
    { value: 7, text: 'Next 7 days' },
    { value: 14, text: 'Next 14 days' },
    { value: 30, text: 'Next 30 days' },
    { value: 60, text: 'Next 60 days' },
    { value: 90, text: 'Next 90 days' },
    { value: 999, text: 'All' }
  ];

  medicationCountOptions = [
    { value: null, text: 'All' },
    { value: { min: 2, max: 3 }, text: '2-3 medications' },
    { value: { min: 4, max: 5 }, text: '4-5 medications' },
    { value: { min: 6, max: null }, text: '6+ medications' }
  ];

  dateSpanOptions = [
    { value: 'all', text: 'All' },
    { value: '7-14', text: '7-14 days apart' },
    { value: '15-30', text: '15-30 days apart' },
    { value: '30+', text: '30+ days apart' }
  ];

  private debouncedLoadOpportunities = debounce(300, () => {
    this.currentPage = 1;
    this.loadOpportunities();
  });

  mounted() {
    this.loadOpportunities();
  }

  get emptyHtml() {
      return `<div class="text-center py-5">
        <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
        <h5>No MedSync Opportunities Found</h5>
        <p class="text-muted">
          No patients meet the MedSync opportunity criteria for the selected timeframe.<br />
          Try adjusting the filters or increasing the timeframe.
        </p>
      </div>`;
  }

  async loadOpportunities() {
    this.loading = true;
    try {
      const params: any = {
        daysAhead: this.filters.daysAhead,
        pageSize: this.perPage,
        pageNumber: this.currentPage,
        sortBy: this.sortBy,
        sortDirection: this.sortDesc ? 'DESC' : 'ASC'
      };

      const trimmedSearch = this.searchTerm.trim();
      if (trimmedSearch) {
        params.searchTerm = trimmedSearch;
      }

      if (this.filters.medicationRange !== null) {
        params.minMedications = this.filters.medicationRange.min;
        if (this.filters.medicationRange.max !== null) {
          params.maxMedications = this.filters.medicationRange.max;
        }
      }

      if (this.filters.minDateSpan !== null) {
        params.minDateSpan = this.filters.minDateSpan;
      }

      if (this.filters.maxDateSpan !== null) {
        params.maxDateSpan = this.filters.maxDateSpan;
      }

      const response = await this.$http.get('/api/medsync/opportunities', { params });
      this.opportunities = response.data.opportunities;
      this.totalRows = response.data.total;
    } catch (error) {
      console.error('Error loading MedSync opportunities:', error);
      this.$notification(NotificationOptions.error("Failed to load MedSync opportunities. Please try again."));
    } finally {
      this.loading = false;
    }
  }

  handleFilterChange() {
    this.currentPage = 1;  // Reset to first page
    this.loadOpportunities();
  }

  handleDateSpanChange() {
    const range = this.filters.dateSpanRange;

    if (range === 'all') {
      this.filters.minDateSpan = null;
      this.filters.maxDateSpan = null;
    } else if (range === '7-14') {
      this.filters.minDateSpan = 7;
      this.filters.maxDateSpan = 14;
    } else if (range === '15-30') {
      this.filters.minDateSpan = 15;
      this.filters.maxDateSpan = 30;
    } else if (range === '30+') {
      this.filters.minDateSpan = 30;
      this.filters.maxDateSpan = null;
    }

    this.handleFilterChange();
  }

  handleSearchChange() {
    this.debouncedLoadOpportunities();
  }

  handleSortChange(ctx: { sortBy: string; sortDesc: boolean }) {
    this.sortBy = ctx.sortBy;
    this.sortDesc = ctx.sortDesc;
    this.loadOpportunities();
  }

  handlePageChange(page: number) {
    this.currentPage = page;
    this.loadOpportunities();
  }

  handlePageSizeChange(size: number) {
    this.perPage = size;
    this.currentPage = 1;  // Reset to first page
    this.loadOpportunities();
  }

  formatDate(date: string | null): string {
    if (!date) return '';
    return moment.utc(date).local().format('MM/DD/YYYY');
  }

  getDateSpanVariant(days: number): string {
      if (days <= 7) return 'danger';
      if (days <= 14) return 'warning';
      if (days <= 30) return 'primary';
      return 'info';
  }

  markContacted(opportunity: MedSyncOpportunity) {
    this.selectedOpportunity = opportunity;
    this.showContactModal = true;
  }

  async saveContact() {
    if (!this.selectedOpportunity) return;

    try {
      await this.$http.post(
        `/api/medsync/opportunity/${this.selectedOpportunity.patientId}/contact`,
        {
          notes: this.contactNotes || null
        }
      );

      this.$notification(NotificationOptions.success("Contact recorded successfully."));
      // Reload opportunities to update last offered date
      await this.loadOpportunities();
    } catch (error) {
      console.error('Error saving contact:', error);
      this.$notification(NotificationOptions.error("Failed to save contact. Please try again."));
    }
  }

  resetContactModal() {
    this.selectedOpportunity = null;
    this.contactNotes = '';
  }

  async enrollPatient(opportunity: MedSyncOpportunity) {
    try {
      // Load the full patient object
      const response = await this.$http.get(`/api/patient/${opportunity.patientId}`);
      this.enrollmentPatient = new Patient(response.data);
      this.showEnrollmentModal = true;
    } catch (error) {
      console.error('Error loading patient:', error);
      this.$notification(NotificationOptions.error("Failed to load patient information. Please try again."));
    }
  }

  closeEnrollmentModal() {
    this.showEnrollmentModal = false;
    this.enrollmentPatient = null;
  }

  handleEnrollmentSuccess() {
    this.closeEnrollmentModal();
    // Notification is shown by MedSyncEnrollmentModal with medication count
    // Reload opportunities to remove newly enrolled patient
    this.loadOpportunities();
  }

  async optOutPatient(opportunity: MedSyncOpportunity) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      `Are you sure you want to opt out ${opportunity.patientName} from MedSync opportunities? This will remove them from this list.`,
      {
        title: 'Opt Out of MedSync',
        okVariant: 'danger',
        okTitle: 'Yes, Opt Out',
        cancelTitle: 'Cancel',
        centered: true
      }
    );

    if (!confirmed) return;

    try {
      await this.$http.post(`/api/medsync/opt-out/${opportunity.patientId}`);
      this.$notification(NotificationOptions.success("Patient opted out of MedSync successfully."));
      // Reload opportunities to remove opted-out patient
      await this.loadOpportunities();
    } catch (error) {
      console.error('Error opting out patient:', error);
      this.$notification(NotificationOptions.error("Failed to opt out patient. Please try again."));
    }
  }
}
</script>

<style scoped>
.medsync-opportunities-page {
  padding: 20px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  width: fit-content;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.4rem 0.6rem;
  min-width: 4.5rem;
}

.action-icon {
  font-size: 1.25rem;
}

.action-label {
  font-size: 0.65rem;
  font-weight: bold;
  margin-top: 0.2rem;
  line-height: 1;
}
</style>
